import React, { useEffect } from 'react';
import { UIActions } from '../ui/uiSlice';
import Toast from '../components/Toast';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import topicPath from './url/topicPath';
import postPath from './url/postPath';
import useNavigate from './router/useNavigate';
import authTokenUtil from '../account/authTokenUtil';

const POST_FRAGMENT = gql(`
  fragment usePublicNotice_post on PublicPost {
    id
    ...postPath_post
  }
`);

const TOPIC_FRAGMENT = gql(`
  fragment usePublicNotice_topic on PublicTopic {
    id
    ...topicPath_topic
  }
`);

let hasShownNotice = false;

type Entity =
  | DocumentType<typeof POST_FRAGMENT>
  | DocumentType<typeof TOPIC_FRAGMENT>;

const usePublicNotice = (entity: Entity | undefined) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isNonVisitorUser = useIsNonVisitorUser();

  useEffect(() => {
    if (!entity) return;

    const internalPath = (() => {
      if (entity.__typename === 'PublicTopic') {
        return topicPath(entity);
      }
      if (entity.__typename === 'PublicPost') {
        return postPath(entity);
      }
      return null;
    })();

    if (
      !hasShownNotice &&
      (isNonVisitorUser || authTokenUtil.get()) &&
      internalPath
    ) {
      hasShownNotice = true;

      dispatch(
        UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
          <Toast
            title={`You’re viewing a public ${
              entity.__typename === 'PublicTopic' ? 'topic' : 'post'
            }`}
            message={
              entity.__typename === 'PublicTopic'
                ? 'Switch to the logged-in view to edit or reorganize the topic'
                : 'This is how the page would look to a logged out visitor'
            }
            onDismiss={toastProps.onDismiss}
            onSecondaryClick={() => {
              navigate(internalPath);
              toastProps.onDismiss();
            }}
            secondaryText="Switch to logged in"
          />
        )),
      );
    }
  }, [dispatch, isNonVisitorUser, entity, navigate]);
};

export default usePublicNotice;
